import { useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { api, apiError } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { peso, clock } from "../lib/format";
import { Btn, Money, Stat, cn } from "../components/ui/kit";

const TABS = ["Dashboard", "Menu", "Inventory", "Purchasing", "Audit"];

export default function AdminApp() {
  const [tab, setTab] = useState("Dashboard");
  return (
    <div className="min-h-[calc(100vh-49px)] bg-slate-100 text-slate-900">
      <div className="mx-auto max-w-[1600px] p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="font-head text-2xl font-black">Owner Command Center</h1>
          <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1">
            {TABS.map((t) => (
              <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                className={cn("rounded-full px-4 py-1.5 text-sm font-bold", tab === t ? "bg-indigo-600 text-white" : "text-slate-600")}>{t}</button>
            ))}
          </div>
        </div>
        {tab === "Dashboard" && <Dashboard />}
        {tab === "Menu" && <MenuAdmin />}
        {tab === "Inventory" && <Inventory />}
        {tab === "Purchasing" && <Purchasing />}
        {tab === "Audit" && <Audit />}
      </div>
    </div>
  );
}

function Dashboard() {
  const { data: d } = usePoll(() => api.get("/reports/dashboard").then((r) => r.data), 4000);
  if (!d) return <Loading />;
  const hourData = Array.from({ length: 24 }, (_, h) => ({ h: `${h}:00`, v: d.by_hour[h] || 0 })).filter((x) => x.v > 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat testid="stat-sales" label="Today's Sales" value={peso(d.todays_sales)} accent="text-emerald-600" sub={`${d.orders} orders · avg ${peso(d.average_check)}`} />
        <Stat label="Preparing" value={d.orders_preparing} sub={`avg prep ${d.avg_prep_minutes} min`} accent="text-indigo-600" />
        <Stat label="Tables Occupied" value={d.tables_occupied} accent="text-slate-900" />
        <Stat label="Tips Pool" value={peso(d.tips_total)} accent="text-amber-600" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Sales by Channel">
          <Breakdown obj={d.by_channel} money />
        </Panel>
        <Panel title="Sales by Payment Method">
          <Breakdown obj={d.by_method} money />
        </Panel>
        <Panel title="Payment Status (orders)">
          <Breakdown obj={d.by_payment_status} />
        </Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Sales by Hour">
          {hourData.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hourData}>
                <XAxis dataKey="h" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => peso(v)} />
                <Bar dataKey="v" radius={[6, 6, 0, 0]}>{hourData.map((_, i) => <Cell key={i} fill="#4f46e5" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <div className="space-y-4">
          <Panel title="Top Sellers">
            {d.top_items.length === 0 ? <Empty /> : d.top_items.map((t) => (
              <div key={t.name} className="flex justify-between py-1 text-sm"><span>{t.name}</span><b>{t.qty} sold</b></div>
            ))}
          </Panel>
          <Panel title={`Alerts · ${d.low_stock.length} low · ${d.expiry_alerts.length} expiring`}>
            {d.low_stock.map((l) => <div key={l.ingredient_id} className="flex justify-between py-0.5 text-sm text-rose-600"><span>⚠ {l.name} low</span><span className="font-mono">{l.stock}/{l.min_stock} {l.unit}</span></div>)}
            {d.expiry_alerts.map((e, i) => <div key={i} className="flex justify-between py-0.5 text-sm text-amber-600"><span>⏳ {e.ingredient} ({e.batch_code})</span><span>{e.days_left <= 0 ? "expires today" : `${e.days_left}d left`}</span></div>)}
            {d.low_stock.length + d.expiry_alerts.length === 0 && <Empty text="All stock healthy" />}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function MenuAdmin() {
  const { data: menu, refetch } = usePoll(() => api.get("/menu").then((r) => r.data), 0);
  const save = async (it, patch) => {
    try { await api.patch(`/menu/${it.id}`, patch); toast.success("Saved (logged)"); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Panel title="Menu & Pricing">
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="p-2">Item</th><th>Category</th><th>Price ₱</th><th>Available</th></tr></thead>
          <tbody>
            {(menu?.items || []).map((it) => (
              <tr key={it.id} className="border-t border-slate-100">
                <td className="p-2 font-semibold">{it.name}</td>
                <td className="text-slate-500">{it.category}</td>
                <td>
                  <input defaultValue={it.price} type="number" data-testid={`price-${it.id}`}
                    onBlur={(e) => Number(e.target.value) !== it.price && save(it, { price: Number(e.target.value) })}
                    className="w-24 rounded-lg border border-slate-200 px-2 py-1 font-mono" />
                </td>
                <td>
                  <button data-testid={`avail-${it.id}`} onClick={() => save(it, { available: !it.available })}
                    className={cn("rounded-full px-3 py-1 text-xs font-bold", it.available ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700")}>
                    {it.available ? "Available" : "86'd"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Inventory() {
  const { data: inv, refetch } = usePoll(() => api.get("/inventory").then((r) => r.data), 4000);
  const count = async (ing) => {
    const v = prompt(`Physical count for ${ing.name} (${ing.unit}). Theoretical: ${ing.theoretical_stock}`);
    if (v == null) return;
    const { data } = await api.post(`/inventory/${ing.id}/count`, { actual: Number(v) });
    toast[data.variance < 0 ? "warning" : "success"](`Variance ${data.variance} ${ing.unit}`);
    refetch();
  };
  return (
    <Panel title="Inventory · FIFO Batches (theoretical vs physical)">
      <div className="space-y-2">
        {(inv || []).map((ing) => (
          <div key={ing.id} className="rounded-xl border border-slate-100 p-3" data-testid={`inv-${ing.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-bold">{ing.name}</span>
                <span className="font-mono text-xs text-slate-400">{ing.sku}</span>
                {ing.low && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-700">LOW</span>}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-mono">{ing.theoretical_stock} {ing.unit}</span>
                <span className="text-xs text-slate-400">min {ing.min_stock}</span>
                <button onClick={() => count(ing)} className="rounded-full border border-slate-200 px-2 py-0.5 text-xs font-bold" data-testid={`count-${ing.id}`}>Count</button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {ing.batches.map((b) => (
                <span key={b.id} className={cn("rounded-lg border px-2 py-1 text-xs font-mono", b.qty_remaining <= 0 ? "border-slate-200 text-slate-300 line-through" : "border-slate-200 text-slate-600")}>
                  {b.batch_code}: {b.qty_remaining}{ing.unit} · exp {new Date(b.expiry_date).toLocaleDateString("en-PH", { month: "short", day: "numeric" })}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Purchasing() {
  const { data: low } = usePoll(() => api.get("/inventory/low-stock").then((r) => r.data), 4000);
  const { data: prs, refetch } = usePoll(() => api.get("/purchase-requests").then((r) => r.data), 4000);
  const flow = ["requested", "for_approval", "approved", "ordered", "received"];
  const create = async (l) => {
    await api.post("/purchase-requests", { ingredient_id: l.ingredient_id, qty_requested: l.suggested_qty, reason: "Auto: low stock" });
    toast.success(`Purchase request for ${l.name}`); refetch();
  };
  const advance = async (pr) => {
    const idx = flow.indexOf(pr.status);
    const next = flow[Math.min(idx + 1, flow.length - 1)];
    if (next === "received") {
      await api.post(`/purchase-requests/${pr.id}/receive`, { qty_received: pr.suggested_qty, cost: 0.5, expiry_days: 7 });
      toast.success(`Received → new FIFO batch created`);
    } else {
      await api.post(`/purchase-requests/${pr.id}/status`, { status: next });
    }
    refetch();
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={`Low Stock · ${low?.length || 0}`}>
        {(low || []).length === 0 && <Empty text="Nothing low" />}
        {(low || []).map((l) => (
          <div key={l.ingredient_id} className="flex items-center justify-between border-t border-slate-100 py-2 text-sm first:border-0">
            <span className="font-semibold text-rose-600">{l.name}</span>
            <span className="font-mono text-xs text-slate-500">{l.stock}/{l.min_stock} {l.unit}</span>
            <Btn variant="indigo" className="px-3 py-1 text-xs" data-testid={`pr-create-${l.ingredient_id}`} onClick={() => create(l)}>Request {l.suggested_qty}</Btn>
          </div>
        ))}
      </Panel>
      <Panel title="Purchase Requests">
        {(prs || []).length === 0 && <Empty text="No requests yet" />}
        {(prs || []).map((pr) => (
          <div key={pr.id} className="flex items-center justify-between border-t border-slate-100 py-2 text-sm first:border-0">
            <div><b>{pr.ingredient_name}</b> <span className="text-slate-400">×{pr.qty_requested}{pr.unit}</span></div>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase">{pr.status.replace(/_/g, " ")}</span>
            {pr.status !== "received"
              ? <Btn className="px-3 py-1 text-xs" data-testid={`pr-advance-${pr.id}`} onClick={() => advance(pr)}>→ {flow[flow.indexOf(pr.status) + 1]?.replace(/_/g, " ")}</Btn>
              : <span className="text-xs font-bold text-emerald-600">✓ stocked</span>}
          </div>
        ))}
      </Panel>
    </div>
  );
}

function Audit() {
  const { data: logs } = usePoll(() => api.get("/audit-logs").then((r) => r.data), 5000);
  return (
    <Panel title="Audit Trail">
      <div className="max-h-[70vh] overflow-auto font-mono text-xs">
        {(logs || []).length === 0 && <Empty text="No actions yet" />}
        {(logs || []).map((l) => (
          <div key={l.id} className="flex flex-wrap gap-2 border-t border-slate-100 py-1.5 first:border-0">
            <span className="text-slate-400">{clock(l.timestamp)}</span>
            <span className="font-bold text-indigo-600">{l.action}</span>
            <span>{l.entity}</span>
            <span className="text-slate-500">by {l.who}</span>
            {l.before != null && <span className="text-rose-500">{JSON.stringify(l.before)}→{JSON.stringify(l.after)}</span>}
            {l.reason && <span className="text-amber-600">({l.reason})</span>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

// shared
function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-3 font-head text-sm font-black uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </div>
  );
}
function Breakdown({ obj, money }) {
  const entries = Object.entries(obj || {}).filter(([, v]) => v);
  if (!entries.length) return <Empty />;
  return entries.map(([k, v]) => (
    <div key={k} className="flex justify-between py-1 text-sm"><span className="text-slate-600">{k}</span><b className="font-mono">{money ? peso(v) : v}</b></div>
  ));
}
function Empty({ text = "No data yet" }) { return <p className="py-4 text-center text-sm text-slate-400">{text}</p>; }
function Loading() { return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" /></div>; }
